// EnrollmentTab.js

import { useState, useEffect } from 'react';
import { getIndividualEnrollment } from '../../services/individualService';
import { queryPlanTypes } from '../../services/systemService';
import Spinner from '../ui/spinners/Spinner';
import style from './IndividualPage.module.scss';
import { SearchableDropdown } from '../helpers/SearchableDropdown_style_b_v2';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { getText } from '../../i18n';

const getTenantId = () => +window.localStorage.getItem('tenantId');

const EnrollmentTab = ({ individualId }) => {
    const [enrollmentData, setEnrollmentData] = useState([]);
    const [filteredEnrollmentData, setFilteredEnrollmentData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [asOfDate, setAsOfDate] = useState(
        new Date().toISOString().slice(0, 10),
    );
    const [selectedPlanType, setSelectedPlanType] = useState([]);
    const [planTypeOptions, setPlanTypeOptions] = useState([]);
    const [resetDropdown, setResetDropdown] = useState(false);
    const hasMultiplePlanTypesSelected = selectedPlanType.length >= 1;
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchEnrollmentData();
        loadPopulationGroups(individualId, getTenantId());
    }, [individualId]);

    useEffect(() => {
        filterEnrollmentData();
    }, [asOfDate, selectedPlanType, enrollmentData]);

    const loadPopulationGroups = async () => {
        try {
            const groups = await queryPlanTypes();
            const optionGroup = groups.map(group => ({
                id: group.id,
                name: group.name,
            }));
            setPlanTypeOptions(optionGroup);
        } catch (error) {
            console.error('Could not fetch plan types', error);
        }
    };

    const fetchEnrollmentData = async (page = 1) => {
        setIsLoading(true);
        try {
            const data = await getIndividualEnrollment(individualId, {
                page,
                pageSize: 10,
            });
            setEnrollmentData(data.items || data);
            setTotalPages(data.pageTotal || data.totalPages || 1);
        } catch (error) {
            console.error(
                'Error retrieving individual enrollment data:',
                error,
            );
            setError(
                'Could not fetch enrollment data. Please try again later.',
            );
        } finally {
            setIsLoading(false);
        }
    };

    const onPageChange = newPage => {
        setCurrentPage(newPage);
        fetchEnrollmentData(newPage);
    };

    useEffect(() => {
        fetchEnrollmentData(currentPage);
    }, [individualId, currentPage]);

    const filterEnrollmentData = () => {
        let filtered = enrollmentData;

        if (asOfDate && selectedPlanType.length === 0) {
            filtered = filtered.filter(enrollment => {
                const startDate = new Date(enrollment.startDate);
                const endDate = new Date(enrollment.endDate);
                const selectedDate = new Date(asOfDate);
                return selectedDate >= startDate && selectedDate <= endDate;
            });
        }

        if (selectedPlanType.length > 0) {
            const selectedPlanTypeIds = selectedPlanType.map(
                planType => planType.id,
            );
            filtered = filtered.filter(enrollment =>
                selectedPlanTypeIds.includes(enrollment.planTypeId),
            );
        }

        setFilteredEnrollmentData(filtered);
    };

    const handlePlanTypeChange = selectedOptions => {
        setSelectedPlanType(selectedOptions);

        if (selectedOptions.length > 0) {
            setAsOfDate('');
        }
    };

    const handleDateChange = date => {
        setAsOfDate(date);
        setSelectedPlanType([]);
        setResetDropdown(prev => !prev);
    };

    const formatDateUTC = isoString => {
        if (!isoString) return '';
        const date = new Date(isoString);
        const year = date.getUTCFullYear();
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = date.getUTCDate().toString().padStart(2, '0');
        return `${month}/${day}/${year}`;
    };

    return (
        <div style={{ width: '100%' }}>
            <div
                className={`${style.filters} mb-5 mt-0 justify-content-center`}
                style={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}
            >
                <div
                    className={`form-group col-12 col-sm-12 col-md-12 col-lg-4 mb-3 d-flex flex-column ${style.asOfDateField}`}
                >
                    <label htmlFor="asOfDate">
                        {getText('msg_46c5bfcad8eb')}
                    </label>
                    <CustomDatePicker
                        className={`form-control`}
                        selectedDate={asOfDate}
                        onDateChange={handleDateChange}
                        isActive={!hasMultiplePlanTypesSelected}
                    />
                </div>

                <div
                    className={`form-group col-1 col-sm-1 col-md-1 col-lg-1 ${style.filterItem} text-center my-auto`}
                >
                    <label htmlFor="asOfDate">
                        {getText('msg_1758356db217')}
                    </label>
                </div>

                <div
                    className={`form-group col-12 col-sm-12 col-md-12 col-lg-4 mb-3 ${style.filterItem}`}
                >
                    <label htmlFor="planType">
                        {getText('msg_a4bf793251b4')}
                    </label>
                    <SearchableDropdown
                        key={resetDropdown}
                        options={planTypeOptions}
                        updateOptions={handlePlanTypeChange}
                        showSelectionBox={false}
                        resetTrigger={resetDropdown}
                        showSelectedCount={true}
                        placeholder={getText('msg_a3fd55164622')}
                        selectedOptions={selectedPlanType}
                        isActive={selectedPlanType.length === 0}
                    />
                </div>
            </div>

            {isLoading ? (
                <Spinner width={50} className={style.loadingSpinner} />
            ) : (
                <>
                    <div style={{ width: '100%', cursor: 'none' }}>
                        <table
                            className="table align-middle ing-table-striped2"
                            style={{
                                cursor: 'none',
                                width: '100%',
                                maxWidth: '100%',
                                tableLayout: 'fixed',
                                minWidth: '100%',
                            }}
                        >
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th
                                        className="item-page-name2"
                                        style={{ paddingLeft: '24px' }}
                                    >
                                        {getText('msg_45061ce8ef30')}
                                    </th>
                                    <th className="item-page-name2">
                                        {getText('msg_ec0632cbbffa')}
                                    </th>
                                    <th
                                        className="item-page-name2"
                                        style={{ paddingLeft: '40px' }}
                                    >
                                        {getText('msg_96791b6925a4')}
                                    </th>
                                    <th
                                        className="item-page-name2"
                                        style={{ paddingLeft: '20px' }}
                                    >
                                        {getText('msg_a40f380753b0')}
                                    </th>
                                    <th
                                        className="item-page-name2"
                                        style={{ textAlign: 'left' }}
                                    >
                                        {getText('msg_e025f749aea0')}
                                    </th>
                                    <th
                                        className="item-page-name2"
                                        style={{ textAlign: 'left' }}
                                    >
                                        {getText('msg_09f1ba4d03fb')}
                                    </th>
                                    <th
                                        className="item-page-name2"
                                        style={{ paddingLeft: '28px' }}
                                    >
                                        {getText('msg_5bd44ebe63cd')}
                                    </th>
                                    <th className="item-page-name2">
                                        {getText('msg_80e13549b47a')}
                                    </th>
                                    <th className="item-page-name2">
                                        {getText('msg_3f18c98c7e88')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEnrollmentData.map((plan, index) => (
                                    <tr key={index}>
                                        <td
                                            className="item-page-name2"
                                            style={{ paddingLeft: '24px' }}
                                        >
                                            {plan.planType}
                                        </td>
                                        <td className="item-page-name2">
                                            {plan.plan}
                                        </td>
                                        <td
                                            className="item-page-name3"
                                            style={{ paddingLeft: '40px' }}
                                        >
                                            {formatDateUTC(plan.startDate)}
                                        </td>
                                        <td
                                            className="item-page-name3"
                                            style={{ paddingLeft: '20px' }}
                                        >
                                            {formatDateUTC(plan.endDate)}
                                        </td>
                                        <td
                                            className="item-page-name3"
                                            style={{ textAlign: 'center' }}
                                        >
                                            {plan.employeeCost?.toFixed(2)}
                                        </td>
                                        <td
                                            className="item-page-name3"
                                            style={{ textAlign: 'center' }}
                                        >
                                            {plan.employerCost?.toFixed(2)}
                                        </td>
                                        <td
                                            className="item-page-name3"
                                            style={{ paddingLeft: '28px' }}
                                        >
                                            {plan.tier || '-'}
                                        </td>
                                        <td
                                            className="item-page-name3"
                                            style={{
                                                textAlign: 'left',
                                                paddingRight: '32px',
                                            }}
                                        >
                                            {plan.volume ?? '-'}
                                        </td>
                                        <td
                                            className="item-page-name3"
                                            style={{
                                                textAlign: 'left',
                                                paddingRight: '32px',
                                            }}
                                        >
                                            {plan.memo ?? '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {filteredEnrollmentData.length === 0 && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={getText('msg_aca14ec90fb5')}
                            description={getText('msg_a2b3c4d5e6f7')}
                        />
                    )}
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={onPageChange}
                    />
                </>
            )}
        </div>
    );
};

export default EnrollmentTab;
